import { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import { configurationApi } from '@/services/configuration.api';
import { LoadingState } from '@/components/common/LoadingState';
import { ErrorState } from '@/components/common/ErrorState';
import { LayoutOptionCard } from './LayoutOptionCard';
import type { LayoutOption } from '@/types';

interface SceltaLayoutScreenProps {
  configId: number;
  /** Chiamato dopo aver impostato il layout (il menu del wizard si sblocca). */
  onConfirmed: () => void;
}

/**
 * Primo step di LAYOUT: "Scelta Layout / Distribuzione Interna". Assegna il layoutId
 * (senza, il menu è vuoto). Colonna sx = configurazione di partenza (variante di default);
 * colonna dx = soluzioni alternative come selettore a TAB (una card alla volta), come il legacy
 * (LayoutCardSelector) — bilanciato anche con molte varianti. Port del legacy SceltaLayout.jsx.
 */
export function SceltaLayoutScreen({ configId, onConfirmed }: SceltaLayoutScreenProps) {
  const [layouts, setLayouts] = useState<LayoutOption[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = (): void => {
    setIsLoading(true);
    setError(null);
    configurationApi
      .getLayouts(configId)
      .then((res) => {
        setLayouts(res.layouts);
        setSelectedId(res.layouts.find((l) => l.selected)?.layoutId ?? null);
        // Tab iniziale: la variante consigliata (fra le alternative), altrimenti la prima.
        const alts = res.layouts.filter((l) => !l.isDefault);
        const recommendedIdx = alts.findIndex((l) => l.isRecommended);
        setActiveTab(recommendedIdx >= 0 ? recommendedIdx : 0);
        setIsLoading(false);
      })
      .catch(() => {
        setError('Impossibile caricare i layout disponibili. Riprova.');
        setIsLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configId]);

  const handleConfirm = (): void => {
    if (selectedId == null) return;
    setIsSaving(true);
    configurationApi
      .setLayout(configId, selectedId)
      .then(() => {
        setIsSaving(false);
        onConfirmed();
      })
      .catch(() => {
        setIsSaving(false);
        setToast('Impossibile impostare il layout. Riprova.');
      });
  };

  if (isLoading) return <LoadingState message="Caricamento layout…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!layouts || layouts.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary">
        Non sono presenti layout per questo appartamento.
      </Typography>
    );
  }

  const defaultLayout = layouts.find((l) => l.isDefault) ?? (layouts[0] as LayoutOption);
  const alternatives = layouts.filter((l) => l.layoutId !== defaultLayout.layoutId);
  const activeAlternative = alternatives[Math.min(activeTab, alternatives.length - 1)];

  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.06em' }}>
        Distribuzione interna
      </Typography>
      <Typography variant="h3" gutterBottom>
        Scegli il layout del tuo appartamento
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Conferma la configurazione di partenza oppure scegli una soluzione alternativa a lato.
      </Typography>

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="overline"
            sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', mb: 1, display: 'block' }}
          >
            Il tuo appartamento — la configurazione di partenza
          </Typography>
          <Box sx={{ flex: 1 }}>
            <LayoutOptionCard
              layout={defaultLayout}
              selected={selectedId === defaultLayout.layoutId}
              onSelect={() => setSelectedId(defaultLayout.layoutId)}
              confirmLabel="Conferma questa scelta"
            />
          </Box>
        </Grid>

        {alternatives.length > 0 && activeAlternative && (
          <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', mb: 1, display: 'block' }}
            >
              Soluzioni alternative
            </Typography>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={Math.min(activeTab, alternatives.length - 1)}
                onChange={(_, v: number) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Soluzioni di layout alternative"
              >
                {alternatives.map((l) => (
                  <Tab
                    key={l.layoutId}
                    label={selectedId === l.layoutId ? `Layout ${l.code} ✓` : `Layout ${l.code}`}
                    sx={{
                      minHeight: 48,
                      fontWeight: 600,
                      color: selectedId === l.layoutId ? 'primary.main' : undefined,
                    }}
                  />
                ))}
              </Tabs>
            </Box>
            <Box sx={{ flex: 1 }}>
              <LayoutOptionCard
                layout={activeAlternative}
                selected={selectedId === activeAlternative.layoutId}
                onSelect={() => setSelectedId(activeAlternative.layoutId)}
                confirmLabel="Scegli questo layout"
              />
            </Box>
          </Grid>
        )}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={selectedId == null || isSaving}
          onClick={handleConfirm}
        >
          {isSaving ? 'Salvataggio…' : 'Prosegui'}
        </Button>
      </Box>

      <Snackbar
        open={toast != null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
